import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router, CanActivateChild } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {
  constructor(private auth: AuthenticationService, private router: Router) { }

  private publicOpportunityFallback(url: string): UrlTree | null {
    // /app/candidate/view-opportunity/18
    const m = url.match(/^\/app\/candidate\/view-opportunity\/(\d+)(?:\/)?(?:\?.*)?$/);
    if (!m) return null;

    const id = m[1];
    return this.router.createUrlTree(['/opportunity', id]);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.check(state.url);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.check(state.url);
  }

  private check(url: string): Observable<boolean | UrlTree> {
    // already signed in
    if (this.auth.isAuthenticated()) {
      return of(this.handleUserStatusRouting(url));
    }

    // try refresh
    return this.auth.RefreshAccessToken().pipe(
      map((res: any) => {
        if (res?.succeeded) {
          this.auth.setToken(res.data.token, res.data.refreshToken);
          return this.handleUserStatusRouting(url);
        }

        // refresh failed: if it's view-opportunity, go public
        const fallback = this.publicOpportunityFallback(url);
        if (fallback) return fallback;

        // otherwise, login
        return this.router.createUrlTree(['/auth/login'], { queryParams: { returnUrl: url } });
      }),
      catchError(() => {
        const fallback = this.publicOpportunityFallback(url);
        if (fallback) return of(fallback);

        return of(this.router.createUrlTree(['/auth/login'], { queryParams: { returnUrl: url } }));
      })
    );
  }

  /**
   * Centralized post-auth routing based on user status.
   *
   * Runs on every guarded navigation, so even if the user
   * types a URL manually, we still enforce:
   * - Pending Verification  -> OTP verification
   * - Initial Login         -> onboarding
   * - Activated / others    -> requested route
   */
  private handleUserStatusRouting(url: string): boolean | UrlTree {
    const status = this.auth.getUserStatus();
    const email = this.auth.getUserEmail();
    const role = this.auth.getUserRole();

    // Pending Verification -> always send to OTP (if we know the email)
    if (status === 0 && email) {
      return this.router.createUrlTree(['/auth/register/otp-verification', email]);
    }

    // Initial Login -> onboarding, regardless of which /app URL was requested
    if (status === 1) {
      // Onboarding Next opens the completion wizard. Allow that one visit when the
      // refreshed token still says Initial Login, otherwise the guard sends the user back.
      const onboardingDone = sessionStorage.getItem('ftn_onboarding_completed');
      const leavingOnboarding =
        url.startsWith('/app/candidate') || url.startsWith('/app/completion');
      if (role === 'Candidate' && onboardingDone === '1' && leavingOnboarding) {
        sessionStorage.removeItem('ftn_onboarding_completed');
        return true;
      }
      if (role === 'Candidate') {
        return this.router.createUrlTree(['/auth/register/candidate/onboarding']);
      }
      if (role === 'Provider') {
        return this.router.createUrlTree(['/auth/register/provider/onboarding']);
      }
    }

    // Activated or unknown status -> allow navigation
    return true;
  }
}